import { formatDisplayDate } from "@/lib/format";
/**
 * pages/Material/lib.ts — material 페이지 전용 순수 유틸 + 상수.
 *
 * 도메인에 두지 않는 이유: 표시/편집 결합(라벨, 정렬, 포맷) — UI 결합 헬퍼.
 *
 * vanilla material.html 라인 276–288, 805–913 발췌 후 React로 정리.
 */
import { fmtNum as fmtNumShared } from "@/lib/format";
import type { CurrentPrice, MaterialCategory, MaterialCategoryKind, RawMaterialPrice } from "@/domain/material";
import { UNIT_BY_CATEGORY } from "@/domain/inbound";

/**
 * 자재 단위 lookup — 명시 unit > category 추론 fallback.
 * material_categories 추가 시 unit 직접 지정 가능. 미지정 시 category로 추론.
 */
export function getMatUnit(cat: Pick<MaterialCategory, "unit" | "category">): string {
  if (cat.unit && cat.unit.trim()) return cat.unit;
  return UNIT_BY_CATEGORY[cat.category] ?? "";
}

/** 활성 자재 여부 — is_active === false 만 비활성. 그 외(undefined/true) → 활성 */
export function isMatActive(cat: Pick<MaterialCategory, "is_active">): boolean {
  return cat.is_active !== false;
}

/** 카테고리 표시 순서. 옛 "시멘트"/"혼화재"/"골재" 데이터는 repository alias로 결합제/굵은골재에 흡수. */
export const CATEGORY_ORDER: readonly MaterialCategoryKind[] = [
  "결합제",
  "굵은골재",
  "잔골재",
  "혼화제",
  "기타",
];

/**
 * 사용자 결정 (2026-05-08): 화면 분류는 material_categories.category 필드 그대로 표시.
 * 통합 매핑 안 함 — 데이터에 있는 그대로 그룹.
 *
 * 정렬 순서는 CATEGORY_ORDER 사용. 그룹 순회 헬퍼:
 *
 * ```ts
 * const grouped = groupByCategory(cats);
 * for (const k of CATEGORY_ORDER) if (grouped[k]?.length) ...
 * ```
 */
export function groupByCategory<T extends { category: MaterialCategoryKind }>(
  rows: readonly T[],
): Partial<Record<MaterialCategoryKind, T[]>> {
  const out: Partial<Record<MaterialCategoryKind, T[]>> = {};
  for (const r of rows) {
    if (!out[r.category]) out[r.category] = [];
    out[r.category]!.push(r);
  }
  return out;
}

/** category index for sorting (CATEGORY_ORDER 기준, 미정 카테고리는 후순위) */
export function categoryOrderIndex(cat: MaterialCategoryKind): number {
  const i = CATEGORY_ORDER.indexOf(cat);
  return i < 0 ? 999 : i;
}

/**
 * 자재 분류 select에 노출할 옵션 — "결합제" 통합 후 (2026-05-08).
 * 옛 "시멘트"/"혼화재"는 신규 입력에서 제외 (데이터는 보존, 표시는 결합제로 묶임).
 */
export const VISIBLE_KINDS: readonly MaterialCategoryKind[] = [
  "결합제",
  "굵은골재",
  "잔골재",
  "혼화제",
  "기타",
];

/**
 * 자재별 트리에서 숨길 코드 — 회사에서 안 쓰는 자재 hide.
 * 자재 코드 단일화(2026-05-08) 후 BS는 데이터에서 삭제되었으므로 빈 set.
 * 추후 안 쓰는 자재가 생기면 여기 추가.
 */
export const HIDDEN_CODES: ReadonlySet<string> = new Set<string>();

/**
 * 자재별 트리에서 표시명 오버라이드 — material_categories.name이 옛 라벨일 때의 안전망.
 * 자재 코드 단일화(2026-05-08) 적용 — 신코드 키만. 마이그레이션이 name도 신라벨로 갱신.
 */
export const NAME_OVERRIDES: Readonly<Record<string, string>> = {
  AD1: "혼화제 (일반1)",
  AD2: "혼화제 (고강도)",
  AD3: "혼화제 (일반2)",
};

/** 기간 라벨 → 추천 effective_date (vanilla 라인 279–288) */
export const PERIOD_DATE_HINT: Readonly<Record<string, string>> = {
  "2024-08":    "2024-08-01",
  "2025-08":    "2025-08-01",
  "2026-01":    "2026-01-01",
  "2026-03":    "2026-03-01",
  "2026-04-1w": "2026-04-01",
  "2026-04-2w": "2026-04-08",
  "2026-04-3w": "2026-04-15",
  "2026-04-4w": "2026-04-22",
};

/** ISO timestamp 또는 YYYY-MM-DD 문자열에서 키 정규화 (vanilla 라인 907) */
export function ymKey(v: unknown): string {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})T/);
  if (iso) return iso[1];
  return s;
}

/** Date 객체 또는 문자열 → "YYYY-MM-DD" 표시 (vanilla 라인 898) */
function dateKey(v: string | Date | null | undefined): string {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Date 객체 또는 문자열의 화면 표시. 기간 계산에는 dateKey를 사용한다. */
export function formatDate(v: string | Date | null | undefined): string {
  return formatDisplayDate(dateKey(v));
}

/** 천 단위 콤마 포맷 (vanilla 라인 1239) */
/** @deprecated lib/format 의 fmtNum 과 동일 — 기존 import 호환용 re-export */
export const fmtNum = fmtNumShared;

/**
 * 자재 단위 → kg 환산계수 (1 자재단위 = N kg).
 * material_categories.conversion_factor 우선, 미설정 시 단위로 추정.
 * 추정 불가(예: ㎥) → 0 반환. 호출자가 환산 실패 처리.
 */
export function getKgPerUnit(cat: Pick<MaterialCategory, "unit" | "category" | "conversion_factor">): number {
  if (cat.conversion_factor && cat.conversion_factor > 0) return cat.conversion_factor;
  const unit = getMatUnit(cat);
  switch (unit) {
    case "톤":
    case "ton":
    case "t":
      return 1000;
    case "kg":
      return 1;
    case "L":
    case "l":
      return 1; // 액체 밀도 가정 (1L ≒ 1kg). 정밀 필요 시 conversion_factor 입력
    default:
      return 0; // ㎥ 등 — conversion_factor 필수
  }
}

/** kg → 자재단위. 환산 불가 시 원본 반환 */
export function kgToMatUnit(kg: number, cat: Pick<MaterialCategory, "unit" | "category" | "conversion_factor">): number {
  const factor = getKgPerUnit(cat);
  return factor > 0 ? kg / factor : kg;
}

/** 자재단위 → kg. 환산 불가 시 원본 반환 */
export function matUnitToKg(qty: number, cat: Pick<MaterialCategory, "unit" | "category" | "conversion_factor">): number {
  const factor = getKgPerUnit(cat);
  return factor > 0 ? qty * factor : qty;
}

/**
 * 산출방식 셀 아이콘 — 변경이력 테이블의 좁은 셀 + 툴팁 디자인용.
 * 풀 라벨은 `formatPricingSource`로 title 속성에 부착.
 */
export function pricingSourceIcon(p: RawMaterialPrice): string {
  switch (p.pricing_source) {
    case "wavg":
    case "wavg_simple":
      return "⚖";
    case "manual":
      return "✎";
    default:
      return "—";
  }
}

/**
 * 짧은 라벨 — 인라인 표시용 ("가중평균" / "수동" / "—").
 * 풀 메타(N개월/M건/자동)는 `formatPricingSource`의 툴팁용.
 */
export function pricingSourceShortLabel(p: RawMaterialPrice): string {
  switch (p.pricing_source) {
    case "wavg":
    case "wavg_simple":
      return "가중평균";
    case "manual":
      return "수동";
    default:
      return "—";
  }
}

/**
 * 변경이력 "산출방식" 셀 라벨 — RawMaterialPrice의 pricing_source 스냅샷을 사람이 읽는 문자열로.
 * - "wavg":        "가중평균 N개월 · M건" (+ 폴백/자동 ON 표시)
 * - "wavg_simple": "가중평균 M건"
 * - "manual":      "수동"
 * - 그 외(레거시): "—"
 */
export function formatPricingSource(p: RawMaterialPrice): string {
  switch (p.pricing_source) {
    case "manual":
      return "수동";
    case "wavg": {
      const w = p.wavg_window;
      const b = p.wavg_basis;
      const head = w && b != null
        ? `가중평균 ${w}개월 · ${b}건`
        : w
          ? `가중평균 ${w}개월`
          : "가중평균";
      const fb = p.wavg_fallback ? " (산술평균 폴백)" : "";
      const auto = p.wavg_auto ? " · 자동 ON" : "";
      return `${head}${fb}${auto}`;
    }
    case "wavg_simple":
      return p.wavg_basis != null ? `가중평균 ${p.wavg_basis}건` : "가중평균";
    default:
      return "—";
  }
}

/** 오늘 YYYY-MM-DD */
export function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * CurrentPrice 정렬 — 카테고리 순서 우선, 같은 카테고리 안에서는 sort_order, 마지막 code 사전순.
 * (vanilla 라인 804–813과 동등 + sort_order 우선) — `deriveCurrentPrices`가 이미 sort_order 적용해서
 * 반환하지만, 카테고리 그룹화 안정 정렬을 위해 한 번 더 정렬해도 idempotent.
 */
export function sortCurrentByCategory(rows: CurrentPrice[]): CurrentPrice[] {
  return rows.slice().sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.category);
    const bi = CATEGORY_ORDER.indexOf(b.category);
    const aw = ai < 0 ? 999 : ai;
    const bw = bi < 0 ? 999 : bi;
    if (aw !== bw) return aw - bw;
    const aso = a.sort_order ?? Number.MAX_SAFE_INTEGER;
    const bso = b.sort_order ?? Number.MAX_SAFE_INTEGER;
    if (aso !== bso) return aso - bso;
    return a.code.localeCompare(b.code);
  });
}

/**
 * 피벗 테이블 행 정렬 — 카테고리 순 + raw_materials에만 있고 카테고리 미등록인 코드는 "(미등록)" 행으로 추가.
 * (vanilla 라인 1011–1023 흡수)
 */
export interface PivotRow {
  code: string;
  name: string;
  category: MaterialCategoryKind | "(미등록)";
}

export function buildPivotRows(
  cats: MaterialCategory[],
  prices: RawMaterialPrice[],
): PivotRow[] {
  const out: PivotRow[] = cats
    .slice()
    .sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a.category);
      const bi = CATEGORY_ORDER.indexOf(b.category);
      const aw = ai < 0 ? 999 : ai;
      const bw = bi < 0 ? 999 : bi;
      if (aw !== bw) return aw - bw;
      const aso = a.sort_order ?? Number.MAX_SAFE_INTEGER;
      const bso = b.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (aso !== bso) return aso - bso;
      return a.code.localeCompare(b.code);
    })
    .map((c) => ({ code: c.code, name: c.name, category: c.category }));

  const known = new Set(out.map((r) => r.code));
  for (const p of prices) {
    if (known.has(p.code)) continue;
    out.push({ code: p.code, name: p.name ?? "", category: "(미등록)" });
    known.add(p.code);
  }
  return out;
}

/**
 * raw_materials → 기간(컬럼) 메타 추출 + 적용일자 기준 desc 정렬.
 * (vanilla 라인 977–1001 흡수)
 *
 * `_origLabel/_origDate`는 React에서 별도 state로 관리하므로 여기서는 빼고,
 * 호출 측에서 useState로 트래킹.
 */
export interface PeriodMeta {
  /** 화면/저장 시 사용하는 라벨 (예: "2026-04-3w") */
  label: string;
  /** 적용일자 YYYY-MM-DD (없을 수 있음) */
  effective_date: string;
}

export function buildPeriods(prices: RawMaterialPrice[]): PeriodMeta[] {
  const map = new Map<string, PeriodMeta>();
  for (const r of prices) {
    if (!r.ym) continue;
    const label = ymKey(r.ym);
    if (!label) continue;
    let iso = r.effective_date ? dateKey(r.effective_date) : "";
    if (!iso && /^\d{4}-\d{2}-\d{2}$/.test(label)) iso = label;
    const cur = map.get(label);
    if (!cur) {
      map.set(label, { label, effective_date: iso || PERIOD_DATE_HINT[label] || "" });
    } else if (!cur.effective_date && iso) {
      cur.effective_date = iso;
    }
  }
  const arr = Array.from(map.values());
  arr.sort((a, b) => {
    const da = a.effective_date ? new Date(a.effective_date).getTime() : 0;
    const db = b.effective_date ? new Date(b.effective_date).getTime() : 0;
    if (da !== db) return db - da;
    return a.label < b.label ? 1 : -1;
  });
  return arr;
}

/** (code, ym) 셀 키 — `${code}||${ym}` */
export function cellKey(code: string, ym: string): string {
  return `${code}||${ym}`;
}

/**
 * 피벗 셀 편집 방식: vanilla 1:1 — 편집 모드 OFF가 디폴트, ON일 때만 모든 셀 input + 하단 일괄 저장.
 * 향후 옵션 B(셀 클릭 → 인라인 input 1개만 활성, blur 즉시 upsert)로 마이그레이션 가능 —
 * useRawMaterials 구독은 그대로 두고 HistoryPivotTab의 editMode + rawEdits state만 교체하면 됨.
 */

/** 추세 차트 시계열 포인트 */
export interface TrendPoint {
  date: string;
  ts: number;
  price: number;
  ym: string;
}

function parseDateMs(s: string | undefined): number {
  if (!s) return 0;
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** raw_materials 행 → TrendPoint[] (특정 code, monthsCutoff개월 컷오프, ts asc 정렬) */
export function buildTrendPoints(
  prices: { code: string; effective_date: string; price: number; ym: string }[],
  code: string,
  monthsCutoff?: number,
): TrendPoint[] {
  let cutoff = 0;
  if (monthsCutoff != null) {
    const d = new Date();
    d.setMonth(d.getMonth() - monthsCutoff);
    d.setHours(0, 0, 0, 0);
    cutoff = d.getTime();
  }
  return prices
    .filter((p) => p.code === code)
    .map((p) => {
      const ts = parseDateMs(p.effective_date) || parseDateMs(p.ym) || 0;
      return {
        date: p.effective_date || p.ym,
        ts,
        price: Number(p.price) || 0,
        ym: p.ym,
      };
    })
    .filter((p) => p.ts > 0 && p.ts >= cutoff)
    .sort((a, b) => a.ts - b.ts);
}

/** ms timestamp → "YYYY-MM-DD" (그룹 통합 시점 라벨용) */
function isoFromMs(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * 통합 관리 그룹 — 같은 category 안에서 `unify_group` 동일값끼리 묶음.
 * `unify_enabled === true` 인 자재만 대상이고, 멤버가 2개 이상인 그룹만 반환한다
 * (단일 자재는 그룹 취급 안 함 → 대부분의 자재는 영향 0).
 *
 * 예: AD1·AD3 둘 다 unify_group="혼화제(일반)" → { label:"혼화제(일반)", codes:["AD1","AD3"] }
 *
 * ⚠ 출고 피벗의 `outbound_subgroup` 과는 별개 필드다. 예전엔 한 필드를 겸용해서,
 * 출고 묶음용으로 넣은 값 때문에 모래+부순모래가 단가 통합으로 잘못 묶였다 (2026-08-12 분리).
 */
export interface MaterialSubgroup {
  /** 그룹 고유 키 `${category}||${label}` */
  key: string;
  /** 표시 라벨 = unify_group 값 */
  label: string;
  category: MaterialCategoryKind;
  /** 멤버 자재 코드 (입력 순서 → 호출측에서 정렬된 cats를 넘기면 그 순서 유지) */
  codes: string[];
}

export function buildSubgroups(
  cats: readonly Pick<
    MaterialCategory,
    "code" | "category" | "unify_group" | "unify_enabled" | "is_active"
  >[],
): MaterialSubgroup[] {
  const map = new Map<string, MaterialSubgroup>();
  for (const c of cats) {
    if (!isMatActive(c)) continue;
    if (HIDDEN_CODES.has(c.code.toUpperCase())) continue;
    if (c.unify_enabled !== true) continue;
    const sub = (c.unify_group ?? "").trim();
    if (!sub) continue;
    const key = `${c.category}||${sub}`;
    let g = map.get(key);
    if (!g) {
      g = { key, label: sub, category: c.category, codes: [] };
      map.set(key, g);
    }
    g.codes.push(c.code);
  }
  return Array.from(map.values()).filter((g) => g.codes.length >= 2);
}

/**
 * 그룹(여러 code) 통합 추세 포인트.
 *
 * 1. 멤버 code들의 effective_date 합집합을 시점 축으로.
 * 2. 각 시점에서 code별 "그 시점 유효(≤시점) 최신 단가" forward-fill.
 * 3. 그 단가들을 `weights`(입고량 비중, 고정) 가중평균 → 통합 라인 1점.
 *    그 시점에 유효 단가가 있는 멤버의 가중치 합으로 정규화. 가중치 합 0이면 단순평균 폴백.
 *
 * @param weights code → 가중치(입고량). 미존재/0 자재는 단순평균 쪽으로 흡수.
 */
export function buildGroupTrendPoints(
  prices: { code: string; effective_date: string; price: number; ym: string }[],
  codes: string[],
  weights: Record<string, number>,
  monthsCutoff?: number,
): TrendPoint[] {
  const codeSet = new Set(codes);
  const byCode = new Map<string, { ts: number; price: number }[]>();
  for (const c of codes) byCode.set(c, []);
  for (const p of prices) {
    if (!codeSet.has(p.code)) continue;
    const ts = parseDateMs(p.effective_date) || parseDateMs(p.ym) || 0;
    if (ts <= 0) continue;
    byCode.get(p.code)!.push({ ts, price: Number(p.price) || 0 });
  }
  for (const arr of byCode.values()) arr.sort((a, b) => a.ts - b.ts);

  const tsSet = new Set<number>();
  for (const arr of byCode.values()) for (const r of arr) tsSet.add(r.ts);
  const allTs = Array.from(tsSet).sort((a, b) => a - b);

  let cutoff = 0;
  if (monthsCutoff != null) {
    const d = new Date();
    d.setMonth(d.getMonth() - monthsCutoff);
    d.setHours(0, 0, 0, 0);
    cutoff = d.getTime();
  }

  const out: TrendPoint[] = [];
  for (const ts of allTs) {
    let wsum = 0;
    let wacc = 0;
    let simpleSum = 0;
    let n = 0;
    for (const c of codes) {
      const arr = byCode.get(c)!;
      let val: number | null = null;
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i].ts <= ts) {
          val = arr[i].price;
          break;
        }
      }
      if (val == null) continue; // 이 시점엔 아직 단가 없음 (forward-fill 불가)
      const w = weights[c] ?? 0;
      wacc += val * w;
      wsum += w;
      simpleSum += val;
      n += 1;
    }
    if (n === 0) continue;
    const price = wsum > 0 ? wacc / wsum : simpleSum / n;
    out.push({ ts, price, date: isoFromMs(ts), ym: "" });
  }
  // forward-fill은 전체 이력 사용, 표시 컷오프만 마지막에 적용
  return out.filter((p) => p.ts >= cutoff);
}
